import { EqFilterType } from '@shure/shared/angular/data-access/system-api/core';
import * as sysApiCore from '@shure/shared/angular/data-access/system-api/core';
import {
	mapAnalogLevelFromSysApi,
	mapChannelGroupFromSysApi,
	mapDanteEncryptionStatusFromSysApi,
	mapInputSourceFromSysApi,
	mapOutputSignalPathFromSysApi,
	mapPcPeripheralFromSysApi,
	mapPcPeripheralOptionsFromSysApi,
	mapValueOrDefault,
	mapValueOrUnsupported,
	mapMicStatusFromSysApi,
	mapAnalogInputUseFromSysApi,
	mapSpeakerTypeToChannelLoudspeaker,
	mapSpatialSeparationFromSysApi
} from '@shure/shared/angular/data-access/system-api/mappers';
import { channelLoudspeakerOrder, DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';

import {
	RfChannelFeatures,
	DspDeviceChannel,
	DspDeviceChannelConstraints,
	DspDeviceChannelDspFeatures
} from '../../api';
import {
	ChannelInfoChannelFragment,
	ChannelInfoRfChannelFragment
} from '../graphql/generated/channel-info-channel.generated';

function mapChannelConstraints(fragment: ChannelInfoChannelFragment): DspDeviceChannelConstraints {
	return {
		analogLevel:
			fragment.features.analogLevel &&
			fragment.description.features.analogLevel.supported &&
			fragment.description.constraints.analogLevel
				? fragment.description.constraints.analogLevel.level.options.map((o) => mapAnalogLevelFromSysApi(o))
				: undefined,
		gain:
			fragment.description.features.gain.supported && fragment.description.constraints.gain?.gain.range
				? fragment.description.constraints.gain.gain.range
				: { max: 0, min: 0, step: 0 },
		meter:
			fragment.description.features.rms.supported && fragment.description.constraints.rms
				? fragment.description.constraints.rms.level.range
				: { max: 0, min: -60, step: 1 },
		inputSource: fragment.description.features.inputSource.supported
			? {
					options:
						fragment.description.constraints.inputSource?.source?.options.map((option) =>
							mapInputSourceFromSysApi(option)
						) ?? []
			  }
			: { options: [] },
		outputPath: fragment.description.features.outputPath.supported
			? {
					options:
						fragment.description.constraints.outputPath?.path.options.map((option) =>
							mapOutputSignalPathFromSysApi(option)
						) ?? []
			  }
			: { options: [] },
		passiveSpeakers: fragment.description.features.passiveSpeakers.supported
			? {
					options: Object.values(sysApiCore.SpeakerType)
						.map((speaker) => mapSpeakerTypeToChannelLoudspeaker(speaker))
						.sort((a, b) => {
							return channelLoudspeakerOrder.indexOf(a) - channelLoudspeakerOrder.indexOf(b);
						})
			  }
			: { options: [] }
	};
}

function mapEqualizer(
	fragment: ChannelInfoChannelFragment
): DspDeviceChannelDspFeatures['equalizer'] | null | undefined {
	const filters = fragment.features.equalizer?.filters || [];
	const gainStep = filters[0]?.description?.constraints?.gain?.gain?.range.step;
	return mapValueOrUnsupported(fragment, 'equalizer', () => ({
		// 'enabled' is currently not working when querying sys-api,
		// so we need to check if any filter is enabled.
		enabled: filters.some((f) => f.features.configuration?.enabled || false),
		isPeq: filters.some(
			(f) => f.description.constraints.configuration?.filterType?.options?.includes(EqFilterType.Parametric)
		),
		gainStepConstraint: gainStep ?? 0.1
	}));
}

export function mapChannelInfoChannelFromSysApi(channelFragment: ChannelInfoChannelFragment): DspDeviceChannel {
	const features = channelFragment.features;
	const carriedByRfChannels = features.carriedByRfChannels;

	return {
		id: channelFragment.id,
		group: mapChannelGroupFromSysApi(channelFragment.description.groupName),
		constraints: mapChannelConstraints(channelFragment),
		features: {
			name: mapValueOrDefault(features.name?.name),
			danteEncryptionStatus: mapDanteEncryptionStatusFromSysApi(
				channelFragment.features.danteEncryptionStatus?.status
			),
			gain: channelFragment.description.features.gain.supported && features.gain ? features.gain.gain : undefined,
			muted: features.mute?.muted || false,
			postGateMute: channelFragment.description.features.postGateMute.supported
				? {
						muted: features.postGateMute?.muted ?? false,
						isMissing: features.postGateMute === null
				  }
				: undefined,
			autoMute:
				channelFragment.description.features.autoMute.supported && features.autoMute
					? features.autoMute.enabled
					: undefined,
			analogLevel:
				channelFragment.description.features.analogLevel.supported && features.analogLevel
					? mapAnalogLevelFromSysApi(features.analogLevel?.level)
					: undefined,
			pcPeripheral:
				channelFragment.description.features.pcPeripheral.supported && features.pcPeripheral
					? {
							options: mapPcPeripheralOptionsFromSysApi(features.pcPeripheral.peripheralOptions),
							selected: features.pcPeripheral.selectedPeripheral
								? mapPcPeripheralFromSysApi(features.pcPeripheral.selectedPeripheral)
								: undefined
					  }
					: undefined,
			dsp: {
				acousticEchoCancellation: mapValueOrUnsupported(channelFragment, 'acousticEchoCancellation', () => ({
					enabled: features.acousticEchoCancellation?.enabled || false
				})),
				automaticGainControl: mapValueOrUnsupported(channelFragment, 'automaticGainControl', () => ({
					enabled: features.automaticGainControl?.enabled || false
				})),
				compressor: mapValueOrUnsupported(channelFragment, 'compressor', () => ({
					enabled: features.compressor?.enabled || false
				})),
				delay: mapValueOrUnsupported(channelFragment, 'delay', () => ({
					enabled: features.delay?.enabled || false
				})),
				equalizer: mapEqualizer(channelFragment),
				limiter: mapValueOrUnsupported(channelFragment, 'limiter', () => ({
					enabled: features.limiter?.enabled || false
				})),
				noiseReduction: mapValueOrUnsupported(channelFragment, 'noiseReduction', () => ({
					enabled: features.noiseReduction?.enabled || false
				})),
				denoiser: mapValueOrUnsupported(channelFragment, 'denoiser', () => ({
					enabled: features.denoiser?.enabled || false
				})),
				deverb: mapValueOrUnsupported(channelFragment, 'deverb', () => ({
					enabled: features.deverb?.enabled || false
				}))
			},
			inputSource: mapValueOrUnsupported(channelFragment, 'inputSource', () => ({
				source: mapInputSourceFromSysApi(features.inputSource?.source) || undefined
			})),
			outputPath: mapValueOrUnsupported(channelFragment, 'outputPath', () => ({
				path: mapOutputSignalPathFromSysApi(features.outputPath?.path) || undefined
			})),
			passiveSpeakers: mapValueOrUnsupported(channelFragment, 'passiveSpeakers', () => ({
				type: mapSpeakerTypeToChannelLoudspeaker(features.passiveSpeakers?.type) || undefined
			})),
			channelConfiguration: channelFragment.description.features.channelConfiguration.supported
				? {
						enabled: features.channelConfiguration?.enabled || false,
						isMissing: features.channelConfiguration === null
				  }
				: undefined,
			hasStereoMeter:
				channelFragment.description.features.stereoPeak.supported &&
				channelFragment.description.features.stereoRms.supported,
			rfChannel: carriedByRfChannels?.rfChannels.length
				? {
						// There will be only one rfChannel associated to one audio channel
						id: carriedByRfChannels?.rfChannels.length === 1 ? carriedByRfChannels.rfChannels[0].id : '',
						features: mapRfChannelFeaturesFromSysApi(carriedByRfChannels?.rfChannels[0]),
						isMissing: !channelFragment.description.features.carriedByRfChannels.supported
				  }
				: undefined,
			analogInputUse: mapValueOrUnsupported(channelFragment, 'analogInputUse', () => ({
				mode: mapAnalogInputUseFromSysApi(features.analogInputUse?.mode) || undefined
			})),
			channelBonding:
				channelFragment.description.features.channelBonding.supported &&
				features.channelBonding?.spatialSeparation
					? {
							spatialSeparation: mapSpatialSeparationFromSysApi(
								features.channelBonding.spatialSeparation
							),
							isMissing: !channelFragment.description.features.channelBonding.supported
					  }
					: undefined
		}
	};
}

export function mapRfChannelFeaturesFromSysApi(rfChannelSysApi: ChannelInfoRfChannelFragment): RfChannelFeatures {
	const {
		features: { linkedTransmitter }
	} = rfChannelSysApi;
	return {
		device: {
			id: linkedTransmitter?.linkage?.device?.id ?? '',
			status: mapMicStatusFromSysApi(linkedTransmitter?.linkage?.device?.features?.micStatusV2?.status),
			deviceState: linkedTransmitter?.linkage?.device
				? linkedTransmitter?.linkage?.device?.status
				: DeviceStatus.Offline
		}
	};
}
